import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Swal from 'sweetalert2';

// Generate a UUID for the new customer (they don't have a Supabase account)
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const NuevoCliente = () => {
  const navigate = useNavigate();

  // Form state
  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_type: '',
    billing_type: '',
    address: '',
    city: '',
    region: '',
    postal_code: '',
    country_code: '',
    fiscal_identification: '',
  });

  // Validation errors
  const [errors, setErrors] = useState({});

  // Submitting state
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  // Validate form
  const validate = () => {
    const newErrors = {};

    if (!form.customer_name.trim()) {
      newErrors.customer_name = 'El nombre es obligatorio.';
    }

    if (!form.customer_email.trim()) {
      newErrors.customer_email = 'El email es obligatorio.';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.customer_email.trim())) {
        newErrors.customer_email = 'Formato de email no válido.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      setSubmitting(true);
      const response = await api.post('/admin/customers', {
        user_id: generateUUID(),
        customer_type: form.customer_type || undefined,
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim(),
        customer_phone: form.customer_phone.trim() || undefined,
        customer_details: {
          billing_address: {
            address: form.address.trim() || undefined,
            city: form.city.trim() || undefined,
            region: form.region.trim() || undefined,
            postal_code: form.postal_code.trim() || undefined,
            country_code: form.country_code.trim() || undefined,
          },
          fiscal_identification: form.fiscal_identification.trim() || undefined,
        },
        billing_type: form.billing_type || undefined,
      });

      const newCustomerId = response.data?.customer_id || response.data?.data?.customer_id;

      Swal.fire({
        icon: 'success',
        title: 'Cliente creado',
        text: 'El cliente se ha creado correctamente.',
        timer: 2000,
        showConfirmButton: false,
      });

      if (newCustomerId) {
        navigate(`/admin/clientes/${newCustomerId}`);
      } else {
        navigate('/admin/clientes');
      }
    } catch (err) {
      console.error('Error creating customer:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.error || 'Error al crear el cliente.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100vw',
        px: { xs: 0, md: 2.5 },
        py: { xs: 0, md: 4 },
        pb: 0,
        mr: { xs: -6 },
        pr: { xs: 0 },
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          padding: { xs: '12px 16px', sm: '8px 16px', md: '12px 16px' },
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          backgroundColor: 'white',
          zIndex: 1000,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {/* Left: Back button + Title */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              ml: { xs: '50px', sm: '50px', md: 35.5 },
            }}
          >
            <IconButton
              onClick={() => navigate('/admin/clientes')}
              sx={{ mr: 1, color: '#4F4F4F' }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: '16px', sm: '18px', md: '20px', lg: '22px' },
                  fontWeight: 'medium',
                  color: '#000',
                }}
              >
                Nuevo Cliente
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  fontSize: { xs: '12px', sm: '15px', md: '14px' },
                  color: '#666',
                  mt: { xs: 0, md: 0.3 },
                  display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
                }}
              >
                Registra un nuevo cliente en la plataforma
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Spacer for fixed header */}
      <Box
        sx={{
          height: { xs: '65px', sm: '70px', md: '100px', lg: '90px' },
          '@media (max-width: 600px) and (min-resolution: 1.25dppx)': { height: '65px' },
          '@media (max-width: 600px) and (min-resolution: 1.5dppx)': { height: '90px' },
          '@media (min-width: 600px) and (min-resolution: 1.25dppx)': { height: '75px' },
          '@media (min-width: 900px) and (min-resolution: 1.25dppx)': { height: '95px' },
        }}
      />

      {/* Main Content */}
      <Box sx={{ ml: { xs: -6, md: 0 } }}>
        <Box sx={{ paddingLeft: { xs: 6, md: 2 }, paddingRight: { xs: 0, md: 4 } }}>
          <Paper
            elevation={3}
            component="form"
            onSubmit={handleSubmit}
            sx={{
              p: { xs: 2, md: 3 },
              boxShadow: '0px 2px 25px 0px rgba(98, 113, 137, 0.46)',
              borderRadius: 2.5,
              mb: 3,
            }}
          >
            {/* Basic Info */}
            <Typography
              sx={{
                fontSize: { xs: '16px', md: '18px' },
                fontWeight: 'bold',
                color: '#000',
                mb: 2,
              }}
            >
              Información del Cliente
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Nombre *"
                  value={form.customer_name}
                  onChange={handleChange('customer_name')}
                  fullWidth
                  size="small"
                  error={!!errors.customer_name}
                  helperText={errors.customer_name}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Email *"
                  value={form.customer_email}
                  onChange={handleChange('customer_email')}
                  fullWidth
                  size="small"
                  type="email"
                  error={!!errors.customer_email}
                  helperText={errors.customer_email}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Teléfono"
                  value={form.customer_phone}
                  onChange={handleChange('customer_phone')}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo de cliente</InputLabel>
                  <Select
                    value={form.customer_type}
                    onChange={handleChange('customer_type')}
                    label="Tipo de cliente"
                  >
                    <MenuItem value="Business">Business</MenuItem>
                    <MenuItem value="Consumer">Consumer</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo de Billing</InputLabel>
                  <Select
                    value={form.billing_type}
                    onChange={handleChange('billing_type')}
                    label="Tipo de Billing"
                  >
                    <MenuItem value="Stripe">Stripe</MenuItem>
                    <MenuItem value="AWS">AWS</MenuItem>
                    <MenuItem value="Big">Big</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Identificación Fiscal"
                  value={form.fiscal_identification}
                  onChange={handleChange('fiscal_identification')}
                  fullWidth
                  size="small"
                />
              </Grid>
            </Grid>

            {/* Billing Address */}
            <Typography
              sx={{
                fontSize: { xs: '16px', md: '18px' },
                fontWeight: 'bold',
                color: '#000',
                mt: 4,
                mb: 2,
              }}
            >
              Dirección de Facturación
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Dirección"
                  value={form.address}
                  onChange={handleChange('address')}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Ciudad"
                  value={form.city}
                  onChange={handleChange('city')}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Región"
                  value={form.region}
                  onChange={handleChange('region')}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Código Postal"
                  value={form.postal_code}
                  onChange={handleChange('postal_code')}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Código de País"
                  value={form.country_code}
                  onChange={handleChange('country_code')}
                  fullWidth
                  size="small"
                  placeholder="ES"
                />
              </Grid>
            </Grid>

            {/* Submit */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/admin/clientes')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 'medium',
                  color: '#4F4F4F',
                  borderColor: '#4F4F4F',
                  fontSize: '14px',
                  px: 3,
                  py: 1,
                  borderRadius: '8px',
                  mr: 2,
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={submitting}
                sx={{
                  textTransform: 'none',
                  fontWeight: 'medium',
                  backgroundColor: '#0F52BD',
                  fontSize: '14px',
                  px: 3,
                  py: 1,
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: '#0a3d8f' },
                }}
              >
                {submitting ? 'Creando...' : 'Crear Cliente'}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default NuevoCliente;
